import axios from "axios";
import AuthApi from "@/api/AuthApi";
import AsyncLocalStorage from "@createnextapp/async-local-storage";
import { JWT_TOKEN, JWT_REFRESH_TOKEN, DEVICE_ID } from "../../const";
import router from "../../router";
import qs from "qs";
const API_URL = process.env.API_URL;

import Vue from "vue";

let isTokenRefreshing = false;
const refreshSubscribers = [];

// 대기 콜백은 실행 후 반드시 큐에서 비운다 — 미정리 시 다음 토큰 갱신 때 과거 요청이 중복 재전송됨
const onTokenRefreshed = (accessToken) => {
  const subscribers = refreshSubscribers.splice(0, refreshSubscribers.length);
  subscribers.forEach((callback) => callback(accessToken));
};

// 갱신 실패 시 대기 중인 요청들을 실패로 전파(기존엔 영구 pending으로 앱이 멈췄음)
const onTokenRefreshFailed = (refreshError) => {
  const subscribers = refreshSubscribers.splice(0, refreshSubscribers.length);
  subscribers.forEach((callback) => callback(null, refreshError));
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

export const httpLoginClient = axios.create({
  baseURL: API_URL,
  withCredentials: false,
});

export const httpClient = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  paramsSerializer: function (params) {
    return qs.stringify(params, { arrayFormat: "brackets" });
  },
});

httpClient.interceptors.request.use((config) => {
  // 병합만 한다 — 통째 대입 시 호출부 지정 헤더(Content-Type 등)가 전부 소실됨
  config.headers.Authorization = `Bearer ${localStorage.getItem(JWT_TOKEN)}`;
  return config;
});

httpClient.interceptors.response.use(undefined, async function (error) {
  const originalRequest = error.config;
  const res = error.response ? error.response : null;

  if (res && res.status === 401) {
    // 구독 등록을 갱신 시작 전에 해야 갱신을 촉발한 첫 요청도 재시도된다(기존엔 flush 이후 등록되어 pending)
    const retryOriginalRequest = new Promise((resolve, reject) => {
      addRefreshSubscriber((accessToken, refreshError) => {
        if (refreshError) {
          reject(refreshError);
          return;
        }
        originalRequest.headers.Authorization = "Bearer " + accessToken;
        resolve(axios(originalRequest));
      });
    });
    if (!isTokenRefreshing) {
      isTokenRefreshing = true;
      try {
        const token = await AsyncLocalStorage.getItem(JWT_TOKEN);
        const reToken = await AsyncLocalStorage.getItem(JWT_REFRESH_TOKEN);
        const { content } = await AuthApi.retoken({
          refreshToken: reToken,
          accessToken: token,
          deviceId: DEVICE_ID,
        });
        await AsyncLocalStorage.setItem(JWT_TOKEN, content.accessToken);
        await AsyncLocalStorage.setItem(
          JWT_REFRESH_TOKEN,
          content.refreshToken
        );

        axios.defaults.headers.common.Authorization = `Bearer ${content.accessToken}`;
        // 새로운 토큰으로 지연되었던 요청 진행
        onTokenRefreshed(content.accessToken);
      } catch (refreshError) {
        onTokenRefreshFailed(refreshError);
        if (window.location.pathname !== "/login") {
          // cabinet 스토어에는 qbox 모듈이 없어 기존 store.state.qbox 참조는 TypeError였음 — 제거
          router.push({
            path: `/login?redirectUrl=${window.location.pathname}`,
          });
        }
      } finally {
        // 성공/실패 무관하게 해제 — 기존엔 실패 시 true로 잠겨 이후 모든 401이 영구 대기했음
        isTokenRefreshing = false;
      }
    }
    return retryOriginalRequest;
  } else {
    let msg = res
      ? "서비스 처리중 오류가 발생했습니다."
      : "네트워크 연결을 확인해주세요.";
    if (res && res.data && res.data.msg) {
      msg = res.data.msg;
    }
    Vue.$toast.error(msg);
  }

  return Promise.reject(error);
});

export const httpClientOnlyRefresh = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  timeout: 3000,
  headers: {},
});

export const uploadClient = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

uploadClient.interceptors.request.use((config) => {
  config.headers.Authorization = `Bearer ${localStorage.getItem(JWT_TOKEN)}`;
  return config;
});

uploadClient.interceptors.response.use(undefined, async function (error) {
  const res = error.response ? error.response : null;
  Vue.$toast.clear();
  if (res && res.status === 500 && res.data && res.data.message) {
    Vue.$toast.error(res.data.message);
  } else if (res) {
    Vue.$toast.error("서비스 처리중 오류가 발생했습니다.");
  } else {
    Vue.$toast.error("네트워크 연결을 확인해주세요.");
  }

  return Promise.reject(error);
});

export const downloadClient = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  responseType: "blob",
});

export const downloadClient2 = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  responseType: "blob",
});

downloadClient2.interceptors.request.use((config) => {
  config.headers.Authorization = `Bearer ${localStorage.getItem(JWT_TOKEN)}`;
  return config;
});

downloadClient.interceptors.response.use(undefined, async function (error) {
  Vue.$toast.clear();
  Vue.$toast.error("서비스 처리중 오류가 발생했습니다.");
  return Promise.reject(error);
});

downloadClient2.interceptors.response.use(undefined, async function (error) {
  Vue.$toast.clear();
  Vue.$toast.error("서비스 처리중 오류가 발생했습니다.");
  return Promise.reject(error);
});

export const reportClient = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  responseType: "blob",
});

reportClient.interceptors.request.use((config) => {
  config.headers["Content-Type"] = "application/json";
  config.headers.Authorization = `Bearer ${localStorage.getItem(JWT_TOKEN)}`;
  return config;
});

reportClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    Vue.$toast.clear();
    let msg = "서비스 처리중 오류가 발생했습니다.";

    if (error.response && error.response.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        if (json.msg) {
          msg = json.msg;
        }
      } catch (e) {
        // blob 파싱 실패 시 무시
      }
    }

    Vue.$toast.error(msg);
    return Promise.reject(error);
  }
);
